import posthog from 'posthog-js';

/**
 * Wrapper de product analytics (PostHog). Diseño clave: si no hay `VITE_POSTHOG_KEY` configurada,
 * TODAS las funciones son no-ops — la app corre igual en dev/CI sin cuenta de PostHog ni red.
 *
 * Ver ADR 0010. Eventos emitidos hoy: `checkout_completed`, `tenant_registered`, `user_logged_in`,
 * `invoice_issued`.
 */

let enabled = false;

/**
 * Inicializa PostHog una sola vez. Llamado desde `main.tsx`. Acepta config explícita (para tests);
 * por defecto lee `import.meta.env`. Sin key → no inicializa y queda en modo no-op.
 */
export function initAnalytics(config?: { key?: string; host?: string }): void {
  if (enabled) {
    return;
  }

  const env = import.meta.env as Record<string, string | undefined>;
  const key = config?.key ?? env.VITE_POSTHOG_KEY;
  const host = config?.host ?? env.VITE_POSTHOG_HOST ?? 'https://us.posthog.com';

  if (!key) {
    return;
  }

  posthog.init(key, {
    api_host: host,
    autocapture: false,
    capture_pageview: true,
    person_profiles: 'identified_only',
  });
  enabled = true;
}

/** Emite un evento de producto. No-op si analytics está deshabilitado. */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  if (!enabled) {
    return;
  }
  posthog.capture(event, properties);
}

/** Asocia la sesión a un tenant (distinct id = tenantId). No-op si está deshabilitado. */
export function identifyTenant(tenantId: string, properties?: Record<string, unknown>): void {
  if (!enabled || !tenantId) {
    return;
  }
  posthog.identify(tenantId, properties);
}

/** Limpia la identidad (logout). No-op si está deshabilitado. */
export function resetIdentity(): void {
  if (!enabled) {
    return;
  }
  posthog.reset();
}
